import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  useCreateTargetingSettingMutation,
  useGetTargetingCountriesDropdownQuery,
  useGetTargetingServicesDropdownQuery,
  useUpdateTargetingSettingMutation,
} from "../model/api/api";
import type {
  ITargetingSetting,
  TTargetingSettingModalMode,
  IApiErrorResponse,
} from "../model/types/types";

interface ITargetingSettingModalProps {
  open: boolean;
  mode: TTargetingSettingModalMode;
  selectedSetting: ITargetingSetting | null;
  onClose: () => void;
}

interface ITargetingSettingFormValues {
  serviceId: number | "";
  countryId: number | "";
  isActive: boolean;
}

interface IQueryError {
  data: IApiErrorResponse;
}

const INITIAL_FORM_VALUES: ITargetingSettingFormValues = {
  serviceId: "",
  countryId: "",
  isActive: true,
};

const isQueryError = (error: unknown): error is IQueryError => {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return false;
  }

  const { data } = error;

  return (
    typeof data === "object" &&
    data !== null &&
    "message" in data &&
    typeof data.message === "string"
  );
};

export const TargetingSettingModal = ({
  open,
  mode,
  selectedSetting,
  onClose,
}: ITargetingSettingModalProps) => {
  const isEditMode = mode === "edit";
  const shouldLoadDropdowns = open && !isEditMode;

  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);

  const {
    data: servicesResponse,
    isLoading: isServicesLoading,
    isError: isServicesError,
  } = useGetTargetingServicesDropdownQuery(undefined, {
    skip: !shouldLoadDropdowns,
  });

  const {
    data: countriesResponse,
    isLoading: isCountriesLoading,
    isError: isCountriesError,
  } = useGetTargetingCountriesDropdownQuery(undefined, {
    skip: !shouldLoadDropdowns,
  });

  const [createSetting, { isLoading: isCreating }] =
    useCreateTargetingSettingMutation();

  const [updateSetting, { isLoading: isUpdating }] =
    useUpdateTargetingSettingMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<ITargetingSettingFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: INITIAL_FORM_VALUES,
  });

  const services = servicesResponse?.data.services ?? [];
  const countries = countriesResponse?.data ?? [];
  const defaultServiceId = servicesResponse?.data.default_service_id ?? "";
  const isDropdownsLoading =
    !isEditMode && (isServicesLoading || isCountriesLoading);
  const isDropdownsError = !isEditMode && (isServicesError || isCountriesError);
  const isSaving = isCreating || isUpdating;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isEditMode) {
      if (!selectedSetting) {
        return;
      }

      reset({
        serviceId: selectedSetting.service.id,
        countryId: selectedSetting.country.id,
        isActive: selectedSetting.is_active,
      });

      return;
    }

    if (isDropdownsLoading || isDropdownsError) {
      return;
    }

    reset({
      serviceId: defaultServiceId,
      countryId: "",
      isActive: true,
    });
  }, [
    open,
    isEditMode,
    selectedSetting,
    isDropdownsLoading,
    isDropdownsError,
    defaultServiceId,
    reset,
  ]);

  useEffect(() => {
    if (!open) {
      setIsCloseConfirmationOpen(false);
    }
  }, [open]);

  const closeModal = () => {
    setIsCloseConfirmationOpen(false);
    reset(INITIAL_FORM_VALUES);
    onClose();
  };

  const handleModalClose = () => {
    if (isSaving) {
      return;
    }

    if (isEditMode && isDirty) {
      setIsCloseConfirmationOpen(true);

      return;
    }

    closeModal();
  };

  const handleCancelClose = () => {
    setIsCloseConfirmationOpen(false);
  };

  const handleConfirmClose = () => {
    closeModal();
  };

  const handleFormSubmit: SubmitHandler<ITargetingSettingFormValues> = async (
    values,
  ) => {
    try {
      if (isEditMode) {
        if (!selectedSetting) {
          return;
        }

        await updateSetting({
          id: selectedSetting.id,
          country_id: selectedSetting.country.id,
          is_active: values.isActive,
        }).unwrap();

        enqueueSnackbar("Изменения сохранены", {
          variant: "success",
        });
      } else {
        if (values.serviceId === "" || values.countryId === "") {
          return;
        }

        await createSetting({
          service_id: values.serviceId,
          country_id: values.countryId,
          is_active: true,
        }).unwrap();

        enqueueSnackbar("Страна добавлена", {
          variant: "success",
        });
      }

      closeModal();
    } catch (error: unknown) {
      const message = isQueryError(error)
        ? error.data.message
        : isEditMode
          ? "Не удалось сохранить изменения"
          : "Не удалось добавить страну";

      enqueueSnackbar(message, {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="sm" onClose={handleModalClose}>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <DialogTitle>
            {isEditMode ? "Редактирование страны" : "Добавление страны"}
          </DialogTitle>

          <DialogContent>
            {isDropdownsLoading && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            )}

            {!isDropdownsLoading && isDropdownsError && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                minHeight={200}
              >
                <Typography color="error" textAlign="center">
                  Не удалось загрузить данные формы
                </Typography>
              </Box>
            )}

            {!isDropdownsLoading && !isDropdownsError && (
              <Stack spacing={1} pt={2}>
                {isEditMode ? (
                  <TextField
                    fullWidth
                    disabled
                    label="Рекламный сервис"
                    value={selectedSetting?.service.name ?? ""}
                    helperText=" "
                  />
                ) : (
                  <Controller
                    name="serviceId"
                    control={control}
                    rules={{
                      validate: (value) => value !== "" || "Обязательное поле",
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        select
                        required
                        fullWidth
                        label="Рекламный сервис"
                        name={field.name}
                        value={field.value}
                        inputRef={field.ref}
                        disabled={isSaving}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? " "}
                        SelectProps={{
                          displayEmpty: true,
                        }}
                        onBlur={field.onBlur}
                        onChange={(event) => {
                          const value = event.target.value;

                          field.onChange(value === "" ? "" : Number(value));
                        }}
                      >
                        <MenuItem value="" disabled>
                          Выберите рекламный сервис
                        </MenuItem>

                        {services.map((service) => (
                          <MenuItem key={service.value} value={service.value}>
                            {service.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                )}

                {isEditMode ? (
                  <TextField
                    fullWidth
                    disabled
                    label="Название страны"
                    value={selectedSetting?.country.name ?? ""}
                    helperText=" "
                  />
                ) : (
                  <Controller
                    name="countryId"
                    control={control}
                    rules={{
                      validate: (value) => value !== "" || "Обязательное поле",
                    }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        options={countries}
                        disabled={isSaving}
                        noOptionsText="Страна не найдена"
                        value={
                          countries.find(
                            (country) => country.value === field.value,
                          ) ?? null
                        }
                        getOptionLabel={(option) => option.name ?? ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        onBlur={field.onBlur}
                        onChange={(_, option) => {
                          field.onChange(option?.value ?? "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            inputRef={field.ref}
                            label="Название страны"
                            placeholder="Введите название страны"
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message ?? " "}
                          />
                        )}
                      />
                    )}
                  />
                )}

                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Активно"
                      control={
                        <Switch
                          name={field.name}
                          checked={field.value}
                          inputRef={field.ref}
                          disabled={!isEditMode || isSaving}
                          onBlur={field.onBlur}
                          onChange={(_event, checked) => {
                            field.onChange(checked);
                          }}
                        />
                      }
                    />
                  )}
                />
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              type="button"
              disabled={isSaving}
              onClick={handleModalClose}
            >
              Отмена
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={
                !isValid ||
                isSaving ||
                isDropdownsLoading ||
                isDropdownsError ||
                (isEditMode && !isDirty)
              }
            >
              {isSaving ? "Сохранение..." : "Сохранить"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={isCloseConfirmationOpen}
        fullWidth
        maxWidth="xs"
        onClose={handleCancelClose}
      >
        <DialogTitle>Есть несохранённые изменения. Закрыть?</DialogTitle>

        <DialogActions>
          <Button onClick={handleCancelClose}>Отмена</Button>

          <Button variant="contained" onClick={handleConfirmClose}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
